import { useApi } from '../../api/api-context';
import { useQueryMutation } from '../../api/use-query-mutation';
import { useBudgetId } from '../../budget/budget-store';
import { useHistoryStore } from '../../history/history-store';
import { useQueryClient } from '@tanstack/react-query';
import { logAndThrowError } from '../../utils/errors';
// Combined hook for category mutations
export const useCategoryMutations = (options) => {
    const api = useApi();
    const budgetId = useBudgetId();
    const queryClient = useQueryClient();
    const queryKey = ['categories', budgetId];
    const historyStore = useHistoryStore();
    // API function definitions
    const createCategoryApi = async (data) => {
        await api.post('/category', data);
    };
    const deleteCategoryApi = async (data) => {
        await api.delete(`/category/${data.budgetId}/${data.id}`);
    };
    // Create mutation
    const createOnMutate = (oldData, request) => {
        const newCategory = { ...request };
        // Handle bucket restoration in query cache
        if (request.buckets?.length) {
            const bucketsQueryKey = ['buckets', request.budgetId];
            const existingBuckets = queryClient.getQueryData(bucketsQueryKey) || [];
            // Add the restored buckets to the buckets query data
            const restoredBuckets = request.buckets.map((bucket) => ({
                ...bucket,
                categoryId: request.id,
            }));
            queryClient.setQueryData(bucketsQueryKey, [...existingBuckets, ...restoredBuckets]);
        }
        // If no existing data, just return the new category
        if (!oldData) {
            return [newCategory];
        }
        // Insert the category at the correct position
        const sortedCategories = [...oldData];
        const insertIndex = sortedCategories.findIndex((cat) => cat.position > newCategory.position);
        if (insertIndex === -1) {
            // If no category has a higher position, add to the end
            return [...sortedCategories, newCategory];
        }
        else {
            // Insert at the correct position
            return [...sortedCategories.slice(0, insertIndex), newCategory, ...sortedCategories.slice(insertIndex)];
        }
    };
    const { mutate: createMutate, isPending: isCreatePending } = useQueryMutation(createCategoryApi, queryKey, createOnMutate, options);
    // Delete mutation
    const deleteOnMutate = (oldData, request) => {
        if (!oldData) {
            logAndThrowError('No categories data found');
        }
        const categoryToDelete = oldData.find((category) => category.id === request.id);
        if (!categoryToDelete) {
            logAndThrowError('Category not found');
        }
        // Also remove the buckets from buckets query data
        const bucketsQueryKey = ['buckets', request.budgetId];
        const existingBuckets = queryClient.getQueryData(bucketsQueryKey);
        if (existingBuckets) {
            // Filter out buckets belonging to the deleted category
            const updatedBuckets = existingBuckets.filter((bucket) => bucket.categoryId !== request.id);
            // Update the buckets query data
            queryClient.setQueryData(bucketsQueryKey, updatedBuckets);
        }
        // Return filtered categories as before
        return oldData.filter((category) => category.id !== request.id);
    };
    const { mutate: deleteMutate, isPending: isDeletePending } = useQueryMutation(deleteCategoryApi, queryKey, deleteOnMutate, options);
    // Create with history
    const createWithHistory = async (request) => {
        createMutate(request);
        historyStore.pushEvent({
            description: 'Create Category',
            undo: () => {
                deleteMutate({ id: request.id, budgetId });
            },
            redo: () => {
                createMutate(request);
            },
        });
    };
    // Delete with history
    const deleteWithHistory = async (request) => {
        const oldCategories = queryClient.getQueryData(queryKey);
        const categoryToDelete = oldCategories?.find((category) => category.id === request.id);
        if (!categoryToDelete) {
            logAndThrowError('Category not found');
        }
        // Get the buckets that will be deleted
        const bucketsQueryKey = ['buckets', request.budgetId];
        const existingBuckets = queryClient.getQueryData(bucketsQueryKey);
        const deletedBuckets = existingBuckets?.filter((bucket) => bucket.categoryId === request.id);
        const restoreCategoryRequest = {
            id: categoryToDelete.id,
            budgetId: categoryToDelete.budgetId,
            name: categoryToDelete.name,
            notes: categoryToDelete.notes,
            position: categoryToDelete.position,
            buckets: deletedBuckets?.map((bucket) => ({
                id: bucket.id,
                name: bucket.name,
                notes: bucket.notes,
                position: bucket.position,
                assignments: bucket.assignments || [],
                archived: bucket.archived || false,
                autoAssign: bucket.autoAssign,
                budgetId: categoryToDelete.budgetId,
            })),
        };
        deleteMutate(request);
        historyStore.pushEvent({
            description: `Delete category ${categoryToDelete.name}`,
            undo: () => {
                createMutate(restoreCategoryRequest);
            },
            redo: () => {
                deleteMutate(request);
            },
        });
    };
    return {
        createCategory: createWithHistory,
        deleteCategory: deleteWithHistory,
        isCreatePending,
        isDeletePending,
    };
};
// Convenience hooks for individual operations
export const useCreateCategoryApi = (options) => {
    const { createCategory, isCreatePending } = useCategoryMutations(options);
    return {
        mutate: createCategory,
        isPending: isCreatePending,
    };
};
export const useDeleteCategoryApi = (options) => {
    const { deleteCategory, isDeletePending } = useCategoryMutations(options);
    return {
        mutate: deleteCategory,
        isPending: isDeletePending,
    };
};
